import { P2GBox, P2GButton, P2GDivider, P2GGrid, P2GPaper, P2GText } from '@parcel2go/core-ui';
import { CmsPromotion } from './quote-promotions';
import Price from '~/components/core/price/price';
import { useCallback, useMemo } from 'react';
import { Protection } from '~/lib/types/extra-types';
import { useTranslation } from 'react-i18next';
import { TranslationNamespaces } from '~/lib/constants';
import QuoteCardServiceLogo from '../card/quote-card-logo';
import { hasVAT } from '~/lib/quotes';
import { useTaxName } from '@parcel2go/npm-p2g-app';

export const Promotion = ({
  promotion,
  onClick,
  index,
  incVat
}: {
  index: number;
  promotion: CmsPromotion;
  onClick: (index: number, promotion: CmsPromotion, extras: string[]) => void;
  incVat: boolean;
}) => {
  const { t } = useTranslation([TranslationNamespaces.CHECKOUT]);
  const taxName = useTaxName();

  const onQuoteClick = useCallback(
    (includeExtras: boolean) => {
      onClick(
        index,
        promotion,
        promotion.quote?.availableExtras.filter((x) => includeExtras && x.key === Protection.BASE).map((x) => x.key) ??
          []
      );
    },
    [onClick, promotion, index]
  );

  const net = useMemo(() => {
    let net = promotion.quote?.price.net ?? 0;
    const base = promotion.quote?.availableExtras.find((x) => x.key === Protection.BASE);
    if (base) {
      net += base.price.net;
    }
    return net;
  }, [promotion.quote]);

  const gross = useMemo(() => {
    let gross = promotion.quote?.price.gross ?? 0;
    const base = promotion.quote?.availableExtras.find((x) => x.key === Protection.BASE);
    if (base) {
      gross += base.price.gross;
    }
    return gross;
  }, [promotion.quote]);

  if (!promotion.quote) {
    return null;
  }

  return (
    <P2GBox
      sx={(t) => ({
        overflow: 'hidden',
        borderRadius: t.spacing(1),
        textAlign: 'center',
        boxShadow: t.shadows[3]
      })}
    >
      <P2GPaper dataTestId="promotion">
        <P2GBox style={{ backgroundColor: promotion.fillColour }}>
          <QuoteCardServiceLogo slug={promotion.serviceSlug} height="65px" />
        </P2GBox>
        <P2GBox p={2}>
          <P2GGrid container direction="column" spacing={1}>
            <P2GGrid item>
              <P2GBox style={{ maxWidth: '240px', margin: '0 auto', textAlign: 'center' }}>
                <P2GText
                  variant="h4"
                  dataTestId="promotion-strapline"
                  dangerouslySetInnerHTML={{ __html: promotion.strapLine }}
                />
              </P2GBox>
            </P2GGrid>
            <P2GGrid item>
              {promotion.features.map((feature) => (
                <P2GText component="div" key={feature} variant="caption">
                  {feature}
                </P2GText>
              ))}
            </P2GGrid>
            <P2GGrid item>
              <P2GDivider variant="middle" />
            </P2GGrid>
            <P2GGrid item>
              {incVat || !hasVAT(promotion.quote) ? (
                <Price
                  currency={promotion.quote.currency}
                  value={gross}
                  dataTestId="promotion-price-inc-vat"
                  variant="h4"
                />
              ) : (
                <>
                  <P2GBox>
                    <Price
                      currency={promotion.quote.currency}
                      value={net}
                      dataTestId="promotion-price-exc-vat"
                      variant="h4"
                    />
                    <P2GText variant="caption"> Exc {taxName}</P2GText>
                  </P2GBox>
                  <P2GBox>
                    <Price
                      currency={promotion.quote.currency}
                      value={gross}
                      dataTestId="promotion-price-inc-vat"
                      variant="caption"
                    />
                    <P2GText variant="caption"> Inc {taxName}</P2GText>
                  </P2GBox>
                </>
              )}
            </P2GGrid>
            <P2GGrid item>
              <P2GButton fullWidth variant="primary" onClick={() => onQuoteClick(true)}>
                {t('PROMOTION_BOOK_BUTTON', { defaultValue: 'Book' })}
              </P2GButton>
            </P2GGrid>
          </P2GGrid>
        </P2GBox>
      </P2GPaper>
    </P2GBox>
  );
};
